// ===================================
// UPDATED TOPICS TABLE WITH MANAGE ACTIONS BUTTON - src/components/TopicsTable.tsx
// ===================================
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import TopicsApi from '../services/topicsApi';
import { parseUserPermissions } from '../utils/permissions';
import { Topic, PaginatedResponse, ListTopicsParams } from '../types';
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  RefreshCw,
  Filter,
  ChevronDown,
  Zap
} from 'lucide-react';
import Button from './ui/button';
import Badge from './ui/badge';

interface TopicsTableProps {
  refreshTrigger?: number;
  onTopicSelect?: (topic: Topic) => void;
  onTopicEdit?: (topic: Topic) => void;
  onTopicCreate?: () => void;
  onTopicDelete?: (topic: Topic) => void;  // Changed from (topicId: number) to (topic: Topic)
}

const TopicsTable: React.FC<TopicsTableProps> = ({
  refreshTrigger = 0,
  onTopicSelect,
  onTopicEdit,
  onTopicCreate,
  onTopicDelete
}) => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const navigate = useNavigate();
  const permissions = parseUserPermissions(user);

  // Table state
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  // Filters and pagination
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [sortBy, setSortBy] = useState<'id' | 'name' | 'created_at' | 'updated_at' | 'action_count'>('updated_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Load topics
  const loadTopics = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: ListTopicsParams = {
        business_id: permissions.isGodMode ? undefined : user?.business_id,
        q: search || undefined,
        page,
        limit,
        sort_by: sortBy,
        sort_order: sortOrder
      };

      const response: PaginatedResponse<Topic> = await TopicsApi.getTopics(params);
      
      setTopics(response.items);
      setTotal(response.total);

    } catch (err) {
      const error = TopicsApi.handleApiError(err);
      setError(error.message);
      console.error('Failed to load topics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load topics on mount and when dependencies change
  useEffect(() => {
    loadTopics();
  }, [search, page, sortBy, sortOrder, refreshTrigger, user?.business_id, permissions.isGodMode]);

  // Handle search with debounce
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const handleSearchChange = (value: string) => {
    setSearch(value);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      setPage(1); // Reset to first page on search
    }, 300);
    
    setSearchTimeout(timeout);
  };

  // Handle sort change
  const handleSort = (newSortBy: typeof sortBy) => {
    if (newSortBy === sortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
    setPage(1);
  };

  // Pagination info
  const totalPages = Math.ceil(total / limit);
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Topics</h3>
          <p className="text-sm text-gray-600 mt-1">
            {total} topic{total !== 1 ? 's' : ''} total
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={loadTopics}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          {permissions.canCreate && (
            <Button 
              size="sm"
              onClick={onTopicCreate}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Topic
            </Button>
          )}
        </div>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Search topics by name or description..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-600">Sort by:</span>
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [newSortBy, newSortOrder] = e.target.value.split('-');
              setSortBy(newSortBy as typeof sortBy);
              setSortOrder(newSortOrder as typeof sortOrder);
              setPage(1);
            }}
            className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="updated_at-desc">Recently Updated</option>
            <option value="created_at-desc">Recently Created</option>
            <option value="name-asc">Name (A-Z)</option>
            <option value="name-desc">Name (Z-A)</option>
            <option value="action_count-desc">Most Actions</option>
            <option value="action_count-asc">Fewest Actions</option>
          </select>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">{error}</p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadTopics}
            className="mt-2"
          >
            Try Again
          </Button>
        </div>
      )}

      {/* Loading state */}
      {loading && topics.length === 0 && (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
                <div className="h-8 bg-gray-200 rounded w-20"></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Topics table */}
      {!loading && topics.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Topic Name
                      <ChevronDown className="h-3 w-3" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Business
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('action_count')}
                  >
                    <div className="flex items-center gap-1">
                      Actions
                      <ChevronDown className="h-3 w-3" />
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('updated_at')}
                  >
                    <div className="flex items-center gap-1">
                      Updated
                      <ChevronDown className="h-3 w-3" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {topics.map((topic) => (
                  <tr key={topic.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {topic.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {topic.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {topic.description || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {topic.business_id ? `Business ${topic.business_id}` : 'Global'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={topic.action_count > 0 ? "default" : "secondary"}>
                        {topic.action_count} action{topic.action_count !== 1 ? 's' : ''}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(topic.updated_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onTopicSelect?.(topic)}
                          className="h-8 w-8 p-0"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        
                        {/* Manage Actions Button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/topics/${topic.id}/actions`)}
                          className="h-8 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Manage Actions"
                        >
                          <Zap className="h-3 w-3 mr-1" />
                          {topic.action_count}
                        </Button>
                        
                        {permissions.canUpdate && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onTopicEdit?.(topic)}
                            className="h-8 w-8 p-0"
                            title="Edit Topic"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
{/*                         
                        {permissions.canDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onTopicDelete?.(topic)}  // Changed: now passes topic object
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete Topic"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )} */}
                        {permissions.canDelete && (
  <Button
    variant="ghost"
    size="sm"
    onClick={() => onTopicDelete?.(topic)}
    disabled={topic.action_count > 0}
    className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
    title={topic.action_count > 0 ? `Cannot delete: topic has ${topic.action_count} action(s)` : "Delete Topic"}
  >
    <Trash2 className="h-4 w-4" />
  </Button>
)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {startItem} to {endItem} of {total} results
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  
                  <span className="text-sm text-gray-600">
                    Page {page} of {totalPages}
                  </span>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!loading && topics.length === 0 && !error && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No topics found</h3>
          <p className="text-gray-600 mb-4">
            {search 
              ? `No topics match "${search}"`
              : `You don't have any topics yet.`
            }
          </p>
          {permissions.canCreate && !search && (
            <Button onClick={onTopicCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Topic
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default TopicsTable;